import React, { useCallback } from 'react';
import {
  Row,
  Col,
  Label,
  Input,
  FormGroup
} from 'reactstrap';
import FormInput from '../../common/FormInput';
import SelectInput from '../../common/SelectInput';
import { ProductConfiguratorFormData } from '../ProductConfiguratorModal';

interface ProductProfileSectionProps {
  formData: ProductConfiguratorFormData;
  onFieldChange: (field: keyof ProductConfiguratorFormData, value: any) => void;
}

const ProductProfileSection: React.FC<ProductProfileSectionProps> = ({
  formData,
  onFieldChange
}) => {
  // Handlers for field changes
  const handleInputChange = useCallback((field: keyof ProductConfiguratorFormData) => (value: string) => {
    onFieldChange(field, value);
  }, [onFieldChange]);

  // Handler for SelectInput component
  const handleSelectFormDataChange = useCallback((newData: ProductConfiguratorFormData) => {
    Object.keys(newData).forEach(key => {
      if (newData[key as keyof typeof newData] !== formData[key as keyof typeof formData]) {
        onFieldChange(key as keyof typeof formData, newData[key as keyof typeof newData]);
      }
    });
  }, [formData, onFieldChange]);

  // Mock options - these should come from LOV hooks in real implementation
  const categoryOptions = ['Electronics', 'Clothing', 'Books', 'Home & Garden'];
  const subcategoryOptions = ['Smartphones', 'Laptops', 'Tablets', 'Accessories'];
  const subtypeOptions = ['Regular', 'Premium', 'Basic'];

  return (
    <fieldset className="border rounded p-2">
      <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.9rem' }}>Product Profile</legend>

      {/* Product ID and Category Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <FormInput
            id="productId"
            label="Product Id"
            value={formData.productId}
            onChange={handleInputChange('productId')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <SelectInput
            formData={formData}
            setFormData={handleSelectFormDataChange}
            label="Category"
            id="category"
            options={categoryOptions}
            htmlId="category"
            disabled
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Name and Subcategory Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <FormInput
            id="name"
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            readOnly
            className="bg-light"
            required
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <SelectInput
            formData={formData}
            setFormData={handleSelectFormDataChange}
            label="Subcategory"
            id="subcategory"
            options={subcategoryOptions}
            disabled
            htmlId="subcategory"
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Product Code and Vendor Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <FormInput
            id="productCode"
            label="Product Code"
            value={formData.productCode}
            onChange={handleInputChange('productCode')}
            readOnly
            className="bg-light"
            required
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <FormInput
            id="vendor"
            label="Vendor"
            value={formData.vendor}
            onChange={handleInputChange('vendor')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Subtype and Vendor Product Code Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <SelectInput
            formData={formData}
            setFormData={handleSelectFormDataChange}
            label="Subtype"
            id="subtype"
            options={subtypeOptions}
            htmlId="subtype"
            disabled
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <FormInput
            id="vendorProductCode"
            label="Vendor Product Code"
            value={formData.vendorProductCode}
            onChange={handleInputChange('vendorProductCode')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Description and Brand Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <FormInput
            id="description"
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <FormInput
            id="brand"
            label="Brand"
            value={formData.brand}
            onChange={handleInputChange('brand')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Long Description and Brand Product Code Row */}
      <Row className="g-2 mb-1">
        <Col md={6}>
          <FormInput
            id="longDescription"
            label="Long Description"
            value={formData.longDescription}
            onChange={handleInputChange('longDescription')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
        <Col md={6}>
          <FormInput
            id="brandProductCode"
            label="Brand Product Code"
            value={formData.brandProductCode}
            onChange={handleInputChange('brandProductCode')}
            readOnly
            className="bg-light"
            containerClassName="mb-1"
          />
        </Col>
      </Row>

      {/* Checkboxes Row */}
      <Row className="g-2">
        <Col md={4}>
          <FormGroup check>
            <Label check className="form-check-label">
              <Input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => onFieldChange('active', e.target.checked)}
                disabled
                className="bg-light"
              />
              {' '}Active
            </Label>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup check>
            <Label check className="form-check-label">
              <Input
                type="checkbox"
                checked={formData.orderable}
                onChange={(e) => onFieldChange('orderable', e.target.checked)}
                disabled
                className="bg-light"
              />
              {' '}Orderable
            </Label>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup check>
            <Label check className="form-check-label">
              <Input
                type="checkbox"
                checked={formData.taxable}
                onChange={(e) => onFieldChange('taxable', e.target.checked)}
                disabled
                className="bg-light"
              />
              {' '}Taxable
            </Label>
          </FormGroup>
        </Col>
      </Row>
    </fieldset>
  );
};

export default ProductProfileSection;
